import { Reducer, useReducer } from 'react';
import { TenantConfig } from 'types';
import assertUnreachable from 'util/assertUnreachable';

export interface State {
  visible: boolean;
  updateTenantStatus: 'Idle' | 'Ready' | 'Loading' | 'Success' | 'Error';
  name?: string;
  initTenant?: TenantConfig;
}

interface ActionButtonClicked {
  type: 'ActionButtonClicked';
}

interface ModalClosed {
  type: 'ModalClosed';
}

interface ModalOpened {
  type: 'ModalOpened';
  tenant: TenantConfig;
}

interface UpdateTenantStarted {
  type: 'UpdateTenantStarted';
}

interface UpdateTenantSuccess {
  type: 'UpdateTenantSuccess';
}

interface UpdateTenantError {
  type: 'UpdateTenantError';
}

interface NameChanged {
  type: 'NameChanged';
  name: string;
}

export type Event =
  | ActionButtonClicked
  | ModalClosed
  | ModalOpened
  | UpdateTenantStarted
  | UpdateTenantSuccess
  | UpdateTenantError
  | NameChanged;

const initialState: State = {
  visible: false,
  updateTenantStatus: 'Idle',
};

const reducer = (state: State, event: Event): State => {
  switch (event.type) {
    case 'ActionButtonClicked':
      return {
        ...state,
        updateTenantStatus: 'Ready',
      };
    case 'ModalClosed':
      return initialState;
    case 'ModalOpened':
      return {
        ...initialState,
        visible: true,
        initTenant: event.tenant,
        name: event.tenant.name,
      };
    case 'UpdateTenantStarted':
      return {
        ...state,
        updateTenantStatus: 'Loading',
      };
    case 'UpdateTenantError':
      return {
        ...state,
        updateTenantStatus: 'Error',
      };
    case 'UpdateTenantSuccess':
      return {
        ...state,
        updateTenantStatus: 'Success',
        visible: false,
      };
    case 'NameChanged':
      return {
        ...state,
        name: event.name,
      };
    default:
      return assertUnreachable(event);
  }
};

const useEditTenantModalReducer = () => {
  return useReducer<Reducer<State, Event>>(reducer, initialState);
};

export default useEditTenantModalReducer;
